import { useState, useEffect, useCallback } from 'react';

function StaffMenu() {
  const staff = JSON.parse(localStorage.getItem('staff') || '{}');
  const token = localStorage.getItem('staff_token');
  const [menus, setMenus] = useState([]);
  const [activeMenu, setActiveMenu] = useState(0);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [restaurantName, setRestaurantName] = useState('');

  const loadMenu = useCallback(async (slug) => {
    const res = await fetch((process.env.REACT_APP_API_URL || '') + '/api/public/menu/' + slug);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load menu');
    return data;
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const slugRes = await fetch((process.env.REACT_APP_API_URL || '') + '/api/public/restaurant/' + staff.restaurant_id);
      const slugData = await slugRes.json();
      if (!slugRes.ok) throw new Error('Restaurant not found');

      const menuData = await loadMenu(slugData.slug);
      setRestaurantName(menuData.restaurant.name);
      const allMenus = menuData.menus;
      setMenus(allMenus);
      if (allMenus.length > 0) {
        const allCategories = allMenus[0].categories;
        const allItems = allCategories.flatMap(cat =>
          cat.items.map(item => ({ ...item, category_name: cat.name }))
        );
        setCategories(allCategories);
        setItems(allItems);
      }
    } catch (err) {
      setError(err.message || 'Failed to load menu');
    } finally {
      setLoading(false);
    }
  }, [staff.restaurant_id, loadMenu]);

  useEffect(() => {
    if (!token) {
      window.location.href = '/staff';
      return;
    }
    fetchAll();
  }, [fetchAll, token]);

  const handleMenuChange = (index) => {
    setActiveMenu(index);
    const selectedMenu = menus[index];
    const allCategories = selectedMenu.categories;
    const allItems = allCategories.flatMap(cat =>
      cat.items.map(item => ({ ...item, category_name: cat.name }))
    );
    setCategories(allCategories);
    setItems(allItems);
  };

  const handleToggle = async (item) => {
    setUpdating(item.id);
    try {
      const res = await fetch((process.env.REACT_APP_API_URL || '') + '/api/items/' + item.id + '/availability', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({ is_available: !item.is_available })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to update item');
        return;
      }
      setItems(items.map(i => i.id === item.id ? { ...i, is_available: data.is_available } : i));
      setError('');
    } catch (err) {
      setError('Failed to update item');
    } finally {
      setUpdating(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('staff_token');
    localStorage.removeItem('staff');
    window.location.href = '/staff';
  };

  if (loading) return <div style={styles.centered}>Loading menu...</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>{restaurantName || 'Staff Panel'}</h1>
          <p style={styles.headerSub}>Logged in as {staff.username}</p>
        </div>
        <button style={styles.logout} onClick={handleLogout}>Sign Out</button>
      </div>

      {menus.length > 1 && (
        <div style={styles.tabs}>
          {menus.map((menu, i) => (
            <button
              key={menu.id}
              style={{ ...styles.tab, ...(activeMenu === i ? styles.tabActive : {}) }}
              onClick={() => handleMenuChange(i)}
            >
              {menu.name}
            </button>
          ))}
        </div>
      )}

      <div style={styles.content}>
        {error && <p style={styles.error}>{error}</p>}
        <p style={styles.hint}>Tap an item to toggle its availability.</p>

        {categories.map(cat => {
          const catItems = items.filter(i => i.category_id === cat.id);
          if (catItems.length === 0) return null;
          return (
            <div key={cat.id} style={styles.categoryGroup}>
              <h2 style={styles.categoryTitle}>{cat.name}</h2>
              {catItems.map(item => (
                <div
                  key={item.id}
                  style={{ ...styles.itemCard, opacity: item.is_available ? 1 : 0.6 }}
                  onClick={() => updating === null && handleToggle(item)}
                >
                  <div style={styles.itemInfo}>
                    <p style={styles.itemName}>{item.name}</p>
                    {item.description && <p style={styles.itemDesc}>{item.description}</p>}
                  </div>
                  <div style={styles.itemRight}>
                    {updating === item.id ? (
                      <span style={styles.updating}>Updating...</span>
                    ) : (
                      <span style={item.is_available ? styles.available : styles.unavailable}>
                        {item.is_available ? 'Available' : 'Sold Out'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f5f5f5' },
  centered: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' },
  header: { backgroundColor: '#1a1a1a', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  headerTitle: { color: '#fff', fontSize: '18px', fontWeight: '700', marginBottom: '2px' },
  headerSub: { color: '#aaa', fontSize: '13px' },
  logout: { backgroundColor: 'transparent', border: '1px solid #fff', color: '#fff', padding: '8px 16px', borderRadius: '4px', fontSize: '14px', cursor: 'pointer' },
  tabs: { display: 'flex', backgroundColor: '#fff', borderBottom: '1px solid #eee', overflowX: 'auto' },
  tab: { padding: '14px 20px', border: 'none', borderBottom: '3px solid transparent', backgroundColor: 'transparent', fontSize: '14px', fontWeight: '500', color: '#666', cursor: 'pointer' },
  tabActive: { borderBottomColor: '#1a1a1a', color: '#1a1a1a', fontWeight: '700' },
  content: { maxWidth: '700px', margin: '0 auto', padding: '24px' },
  error: { backgroundColor: '#fdecea', color: '#c0392b', padding: '10px', borderRadius: '4px', marginBottom: '16px', fontSize: '14px' },
  hint: { fontSize: '13px', color: '#888', marginBottom: '20px' },
  categoryGroup: { marginBottom: '28px' },
  categoryTitle: { fontSize: '13px', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' },
  itemCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fff', padding: '16px', borderRadius: '8px', marginBottom: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer' },
  itemInfo: { flex: 1 },
  itemName: { fontSize: '15px', fontWeight: '600', color: '#1a1a1a', marginBottom: '2px' },
  itemDesc: { fontSize: '13px', color: '#888' },
  itemRight: { marginLeft: '16px' },
  available: { fontSize: '13px', backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '6px 12px', borderRadius: '20px', fontWeight: '600' },
  unavailable: { fontSize: '13px', backgroundColor: '#fdecea', color: '#c0392b', padding: '6px 12px', borderRadius: '20px', fontWeight: '600' },
  updating: { fontSize: '13px', color: '#999', padding: '6px 12px' },
};

export default StaffMenu;
